import { Portal, useDisclosure, Popover } from '@chakra-ui/react';
import { IRevenueSchedulePeriod } from '@monetize/types/app';
import { formatCurrency } from '@monetize/utils/core';
import { FC, ReactNode } from 'react';
import { MBox, MFlex, MText, MVStack } from '../Monetize';

interface PeriodAuditPopoverProps {
  period: IRevenueSchedulePeriod;
  children: ReactNode;
}

const DetailRow: FC<{ label: string; value: ReactNode }> = ({
  label,
  value,
}) => (
  <MFlex w="full" justifyContent="space-between" alignItems="center">
    <MText fontSize="sm" fontWeight="semibold">
      {label}
    </MText>
    <MText fontSize="sm">{value}</MText>
  </MFlex>
);

const PeriodAuditPopoverBody: FC<{ period: IRevenueSchedulePeriod }> = ({
  period,
}) => {
  const cumulativeRevenue =
    period.cumulativeRevenue != null
      ? formatCurrency(period.cumulativeRevenue)
      : '—';
  const deferredRevenue =
    period.deferredRevenue != null
      ? formatCurrency(period.deferredRevenue)
      : '—';

  return (
    <MVStack
      justifyContent="flex-start"
      alignItems="flex-start"
      gap="2"
      w="full"
    >
      <DetailRow label="Period Start" value={period.period.start} />
      <DetailRow label="Period End" value={period.period.endInclusive} />
      <DetailRow label="Cumulative Revenue" value={cumulativeRevenue} />
      <DetailRow label="Deferred Revenue" value={deferredRevenue} />
    </MVStack>
  );
};

export const PeriodAuditPopover: FC<PeriodAuditPopoverProps> = ({
  period,
  children,
}) => {
  const { onOpen, onClose, open } = useDisclosure();

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      open={open}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
      restoreFocus
      size="md"
      positioning={{
        strategy: 'fixed',
        placement: 'bottom-start',
        offset: { mainAxis: 4, crossAxis: 0 },
      }}
    >
      <Popover.Trigger asChild>
        <MBox asChild textAlign="left" onClick={onOpen} cursor="pointer">
          <button type="button">{children}</button>
        </MBox>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content width="400px" maxWidth="400px">
            <Popover.Body>
              {open && <PeriodAuditPopoverBody period={period} />}
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};
